import {
  pageFlows,
  apply,
  mul,
  nodeOutline,
  connectorGeometry,
  boundsOfNodes,
  anchorsOf,
  pathCommands,
  type Mat,
  type NodeData,
  type NodeId,
  type Rect,
  type Vec2,
  type PathCmd,
  tracePath,
  isContextContainer,
  layoutNodeText,
  textPadding,
  textBox,
  fillTextRun,
} from '@vellum/core';
import type { App } from '../app.ts';

export const SELECTION_COLOR = '#0d99ff';

/** Frames, components, instances and sections that show their name above them on the canvas. */
export function titledNodes(app: App): NodeData[] {
  const doc = app.doc;
  const out: NodeData[] = [];
  const visit = (parent: NodeId) => {
    for (const n of doc.children(parent)) {
      if (!n.visible) continue;
      if (n.type === 'SECTION') out.push(n);
      else if ((n.type === 'FRAME' || n.type === 'COMPONENT') && isContextContainer(doc, n)) out.push(n);
      else if (n.type === 'INSTANCE') out.push(n);
      if (n.type === 'SECTION') visit(n.id);
    }
  };
  visit(app.editor.pageId);
  return out;
}

export function titleLabel(n: NodeData) {
  return (n.type === 'COMPONENT' ? '◇ ' : n.type === 'INSTANCE' ? '◆ ' : '') + n.name;
}

let measurer: CanvasRenderingContext2D | null = null;
/** Where a layer's canvas title sits, in screen pixels (null when it isn't drawn, e.g. too small or rotated). */
export function titleRect(app: App, n: NodeData): Rect | null {
  const p = apply(mul(app.viewMatrix(), app.doc.worldTransform(n.id)), { x: 0, y: 0 });
  const w = n.width * app.camera.zoom;
  if (w < 24) return null;
  measurer ??= document.createElement('canvas').getContext('2d');
  if (!measurer) return null;
  if (n.type === 'SECTION') {
    measurer.font = '600 13px Inter, sans-serif';
    return { x: p.x, y: p.y - 30, width: Math.min(measurer.measureText(titleLabel(n)).width + 16, w), height: 24 };
  }
  if (n.rotation) return null;
  measurer.font = '500 11px Inter, sans-serif';
  return { x: p.x, y: p.y - 18, width: Math.max(16, Math.min(measurer.measureText(titleLabel(n)).width, n.devStatus ? w - 90 : w)), height: 16 };
}

/** The layer whose canvas title is under screen point s (topmost first). */
export function titleAt(app: App, s: Vec2): NodeData | null {
  const list = titledNodes(app);
  for (let i = list.length - 1; i >= 0; i--) {
    const r = titleRect(app, list[i]);
    if (r && s.x >= r.x && s.x <= r.x + r.width && s.y >= r.y && s.y <= r.y + r.height) return list[i];
  }
  return null;
}
export const COMPONENT_COLOR = '#9747ff';
const HANDLE = 8;

export type HandleId = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w';
export const HANDLES: { id: HandleId; u: number; v: number }[] = [
  { id: 'nw', u: 0, v: 0 },
  { id: 'n', u: 0.5, v: 0 },
  { id: 'ne', u: 1, v: 0 },
  { id: 'e', u: 1, v: 0.5 },
  { id: 'se', u: 1, v: 1 },
  { id: 's', u: 0.5, v: 1 },
  { id: 'sw', u: 0, v: 1 },
  { id: 'w', u: 0, v: 0.5 },
];

/** The selection's transform box: a node-local frame (for single nodes) or the AABB of many. */
export interface SelectionBox {
  /** maps unit box (0..w, 0..h) to page space */
  m: Mat;
  width: number;
  height: number;
  single: NodeData | null;
}

/** FigJam-style "+" handles around a single selected sticky/shape (screen coords). */
export function quickCreateHandles(app: App): { dir: 'N' | 'E' | 'S' | 'W'; p: Vec2 }[] {
  const sel = app.editor.selectedNodes();
  if (sel.length !== 1 || app.tool !== 'select' || app.ui.editingTextId || app.ui.dragging) return [];
  const n = sel[0];
  if (n.type !== 'STICKY' && n.type !== 'SHAPE_WITH_TEXT') return [];
  const b = app.doc.worldBounds(n.id);
  const a = app.pageToScreen({ x: b.x, y: b.y });
  const z = app.camera.zoom;
  const w = b.width * z,
    h = b.height * z;
  if (w < 40 || h < 40) return [];
  const off = 22;
  return [
    { dir: 'N', p: { x: a.x + w / 2, y: a.y - off } },
    { dir: 'E', p: { x: a.x + w + off, y: a.y + h / 2 } },
    { dir: 'S', p: { x: a.x + w / 2, y: a.y + h + off } },
    { dir: 'W', p: { x: a.x - off, y: a.y + h / 2 } },
  ];
}

export function selectionBox(app: App): SelectionBox | null {
  const sel = app.editor.selectedNodes();
  if (!sel.length) return null;
  if (sel.length === 1 && sel[0].type !== 'CONNECTOR') {
    const n = sel[0];
    return { m: app.doc.worldTransform(n.id), width: n.width, height: n.height, single: n };
  }
  const b = boundsOfNodes(
    app.doc,
    sel.map((n) => n.id),
  );
  if (!b) return null;
  if (sel.length === 1) {
    const r = app.doc.worldBounds(sel[0].id);
    return { m: [1, 0, 0, 1, r.x, r.y], width: r.width, height: r.height, single: sel[0] };
  }
  // use true node bounds (not visual bounds with shadows) for multi-select
  let r: Rect | null = null;
  for (const n of sel) {
    const nb = app.doc.worldBounds(n.id);
    r = r ? union(r, nb) : nb;
  }
  return { m: [1, 0, 0, 1, r!.x, r!.y], width: r!.width, height: r!.height, single: null };
}

function union(a: Rect, b: Rect): Rect {
  const x = Math.min(a.x, b.x),
    y = Math.min(a.y, b.y);
  return { x, y, width: Math.max(a.x + a.width, b.x + b.width) - x, height: Math.max(a.y + a.height, b.y + b.height) - y };
}

export function handlePositions(app: App, box: SelectionBox): { id: HandleId; p: Vec2 }[] {
  const view = app.viewMatrix();
  const m = mul(view, box.m);
  return HANDLES.map((h) => ({ id: h.id, p: apply(m, { x: h.u * box.width, y: h.v * box.height }) }));
}

let DPR = 1;
function strokeOutline(ctx: CanvasRenderingContext2D, m: Mat, cmds: PathCmd[]) {
  ctx.save();
  ctx.setTransform(m[0] * DPR, m[1] * DPR, m[2] * DPR, m[3] * DPR, m[4] * DPR, m[5] * DPR);
  ctx.beginPath();
  tracePath(ctx, cmds);
  ctx.restore();
  ctx.stroke();
}

export class OverlayRenderer {
  constructor(private app: App) {}
  private videos: { version: number; ids: NodeId[] } = { version: -1, ids: [] };

  /** Layers with a video fill (they get a play badge); rescanned when the document changes. */
  private videoNodes(): NodeId[] {
    if (this.videos.version !== this.app.docVersion) {
      const ids: NodeId[] = [];
      for (const n of this.app.doc.nodes.values()) if (n.fills.some((p) => p.type === 'IMAGE' && p.video)) ids.push(n.id);
      this.videos = { version: this.app.docVersion, ids };
    }
    return this.videos.ids;
  }

  render(ctx: CanvasRenderingContext2D, dpr: number) {
    const app = this.app;
    const { width, height } = app.viewportSize;
    DPR = dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, width, height);
    const view = app.viewMatrix();
    const doc = app.doc;
    const pageId = app.editor.pageId;
    if (!doc.has(pageId)) return;

    // Layout grids
    if (app.ui.showGrids) for (const n of doc.children(pageId)) this.grids(ctx, n, view);

    // Frame & section titles
    ctx.font = '500 11px Inter, sans-serif';
    ctx.textBaseline = 'bottom';
    // (renamed in place: the input stands in for the title)
    for (const n of titledNodes(app)) if (app.ui.renaming?.id !== n.id || app.ui.renaming.on !== 'canvas') this.title(ctx, n, view);

    const selected = new Set(app.editor.selection);

    // Vote dots: your own while voting is open, everyone's once it ends
    const vote = app.voting;
    if (vote && vote.pageId === pageId) {
      const byNode = new Map<string, { color: string }[]>();
      for (const v of vote.votes) if (!vote.active || v.voter === app.userName) byNode.set(v.nodeId, [...(byNode.get(v.nodeId) ?? []), v]);
      for (const [id, votes] of byNode) {
        if (!doc.has(id)) continue;
        const b = doc.worldBounds(id);
        const tr = apply(view, { x: b.x + b.width, y: b.y });
        const r = 9;
        const shown = votes.slice(0, 6);
        shown.forEach((v, i) => {
          ctx.beginPath();
          ctx.arc(tr.x - 6 - i * (r * 1.3), tr.y + 6, r, 0, Math.PI * 2);
          ctx.fillStyle = v.color;
          ctx.fill();
          ctx.lineWidth = 2;
          ctx.strokeStyle = '#fff';
          ctx.stroke();
        });
        if (!vote.active || votes.length > 6) {
          // the count badge
          const label = String(votes.length);
          ctx.font = '700 11px Inter, sans-serif';
          const w = Math.max(18, ctx.measureText(label).width + 10);
          const x = tr.x - 6 - shown.length * (r * 1.3) - w + 4;
          ctx.fillStyle = '#1e1e1e';
          ctx.beginPath();
          ctx.roundRect(x, tr.y - 3, w, 18, 9);
          ctx.fill();
          ctx.fillStyle = '#fff';
          ctx.textBaseline = 'middle';
          ctx.textAlign = 'center';
          ctx.fillText(label, x + w / 2, tr.y + 6);
          ctx.textAlign = 'left';
        }
      }
    }

    // Remote selections
    for (const p of app.peers.values()) {
      if (p.pageId && p.pageId !== pageId) continue;
      ctx.strokeStyle = p.color;
      ctx.lineWidth = 1.5;
      for (const id of p.selection ?? []) {
        const n = doc.get(id);
        if (!n || doc.pageOf(id)?.id !== pageId) continue;
        this.outline(ctx, n, view);
      }
      if (p.kind === 'agent' && p.selection?.length) {
        const b = boundsOfNodes(doc, p.selection.filter((id) => doc.has(id) && doc.pageOf(id)?.id === pageId));
        if (b) {
          const tr = apply(view, { x: b.x + b.width, y: b.y });
          const label = p.status ? `${p.name} · ${p.status}` : p.name;
          ctx.font = '500 11px Inter, sans-serif';
          this.badge(ctx, tr.x - ctx.measureText(label).width - 10, tr.y - 4, label, p.color, true);
        }
      }
    }

    // Hover
    const hover = app.ui.hoverId ? doc.get(app.ui.hoverId) : null;
    if (hover && !selected.has(hover.id) && !app.ui.dragging) {
      ctx.strokeStyle = hover.type === 'COMPONENT' || hover.type === 'INSTANCE' ? COMPONENT_COLOR : SELECTION_COLOR;
      ctx.lineWidth = 2;
      this.outline(ctx, hover, view);
    }

    // Drop target
    if (app.ui.dropTargetId) {
      const t = doc.get(app.ui.dropTargetId);
      if (t) {
        ctx.strokeStyle = SELECTION_COLOR;
        ctx.lineWidth = 2;
        ctx.setLineDash([]);
        this.outline(ctx, t, view);
      }
    }

    // Selection outlines & box
    const editingText = app.ui.editingTextId;
    ctx.lineWidth = 1;
    for (const id of selected) {
      const n = doc.get(id);
      if (!n) continue;
      ctx.strokeStyle = n.type === 'COMPONENT' || n.type === 'INSTANCE' ? COMPONENT_COLOR : SELECTION_COLOR;
      this.outline(ctx, n, view);
    }
    const box = selectionBox(app);
    if (box && !editingText && app.tool === 'select' && !app.ui.draft && !app.ui.editingPathId) {
      const single = box.single;
      const color = single && (single.type === 'COMPONENT' || single.type === 'INSTANCE') ? COMPONENT_COLOR : SELECTION_COLOR;
      if (single?.type === 'CONNECTOR') this.connectorHandles(ctx, single, view);
      else if (single?.type === 'LINE') this.lineHandles(ctx, single, view, color);
      else {
        const m = mul(view, box.m);
        ctx.strokeStyle = color;
        ctx.lineWidth = 1;
        const corners = [apply(m, { x: 0, y: 0 }), apply(m, { x: box.width, y: 0 }), apply(m, { x: box.width, y: box.height }), apply(m, { x: 0, y: box.height })];
        ctx.beginPath();
        corners.forEach((c, i) => (i ? ctx.lineTo(c.x, c.y) : ctx.moveTo(c.x, c.y)));
        ctx.closePath();
        ctx.stroke();
        const sw = Math.hypot(corners[1].x - corners[0].x, corners[1].y - corners[0].y);
        const sh = Math.hypot(corners[3].x - corners[0].x, corners[3].y - corners[0].y);
        const small = sw < 24 || sh < 24;
        for (const h of handlePositions(app, box)) {
          if (small && h.id.length === 1) continue;
          if ((h.id === 'n' || h.id === 's') && sw < 40) continue;
          if ((h.id === 'e' || h.id === 'w') && sh < 40) continue;
          if (h.id.length === 1) continue; // edge handles are invisible (whole edge is draggable)
          ctx.fillStyle = '#fff';
          ctx.strokeStyle = color;
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.rect(Math.round(h.p.x - HANDLE / 2) + 0.5, Math.round(h.p.y - HANDLE / 2) + 0.5, HANDLE, HANDLE);
          ctx.fill();
          ctx.stroke();
        }
        // size label
        const bottom = [...corners].sort((a, b) => b.y - a.y);
        const mid = { x: (bottom[0].x + bottom[1].x) / 2, y: Math.max(bottom[0].y, bottom[1].y) + 8 };
        const w = single ? single.width : box.width;
        const hgt = single ? single.height : box.height;
        if (!quickCreateHandles(app).length) this.badge(ctx, mid.x, mid.y, `${fmt(w)} × ${fmt(hgt)}`, color, false, true);
      }
    }

    // Prototype flows from the selection
    for (const id of selected) {
      const n = doc.get(id);
      for (const r of n?.reactions ?? []) {
        const dest = 'destinationId' in r.action ? doc.get(r.action.destinationId) : null;
        if (!n || !dest || doc.pageOf(dest.id)?.id !== pageId) continue;
        const a = doc.worldBounds(n.id),
          b = doc.worldBounds(dest.id);
        const p0 = apply(view, { x: a.x + a.width, y: a.y + a.height / 2 });
        const p1 = apply(view, { x: b.x, y: b.y + Math.min(b.height / 2, 40) });
        const dx = Math.max(40, Math.abs(p1.x - p0.x) / 2);
        ctx.strokeStyle = SELECTION_COLOR;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(p0.x, p0.y);
        ctx.bezierCurveTo(p0.x + dx, p0.y, p1.x - dx, p1.y, p1.x, p1.y);
        ctx.stroke();
        ctx.fillStyle = SELECTION_COLOR;
        ctx.beginPath();
        ctx.moveTo(p1.x, p1.y);
        ctx.lineTo(p1.x - 9, p1.y - 5);
        ctx.lineTo(p1.x - 9, p1.y + 5);
        ctx.fill();
        ctx.beginPath();
        ctx.arc(p0.x, p0.y, 4, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    for (const f of pageFlows(doc, pageId)) {
      const b = doc.worldBounds(f.nodeId);
      // top right, clear of the frame's name label
      const p = apply(view, { x: b.x + b.width, y: b.y });
      const label = `▶ ${f.name}`;
      ctx.font = '500 11px Inter, sans-serif';
      this.badge(ctx, p.x - ctx.measureText(label).width - 10, p.y - 4, label, SELECTION_COLOR, true);
    }
    // multi-edit: outline every layer being edited together, and say so on the first
    const together = (app.editor.multiEdit ?? []).filter((id) => doc.has(id) && doc.pageOf(id)?.id === pageId);
    if (together.length) {
      ctx.save();
      ctx.strokeStyle = SELECTION_COLOR;
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 3]);
      for (const id of together) {
        const b = doc.worldBounds(id);
        const a0 = apply(view, { x: b.x, y: b.y }),
          a1 = apply(view, { x: b.x + b.width, y: b.y + b.height });
        ctx.strokeRect(Math.round(a0.x) - 2.5, Math.round(a0.y) - 2.5, a1.x - a0.x + 5, a1.y - a0.y + 5);
      }
      ctx.restore();
      const b = doc.worldBounds(together[0]);
      const p = apply(view, { x: b.x, y: b.y + b.height });
      this.badge(ctx, p.x, p.y + 6, `Editing ${together.length} layers together · Esc to finish`, SELECTION_COLOR);
    }

    // Quick-create handles
    for (const q of quickCreateHandles(app)) {
      ctx.fillStyle = '#fff';
      ctx.strokeStyle = SELECTION_COLOR;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.arc(q.p.x, q.p.y, 9, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(q.p.x - 4, q.p.y);
      ctx.lineTo(q.p.x + 4, q.p.y);
      ctx.moveTo(q.p.x, q.p.y - 4);
      ctx.lineTo(q.p.x, q.p.y + 4);
      ctx.stroke();
    }

    // Image crop mode: show the full image extent
    const cn = app.ui.croppingId ? doc.get(app.ui.croppingId) : null;
    const cp = cn?.fills.find((f) => f.type === 'IMAGE');
    if (cn && cp?.type === 'IMAGE' && cp.crop) {
      // the whole image shows while cropping (load the original for it)
      const img = app.images.get(cp.src) as HTMLImageElement | undefined;
      const nat = app.images.naturalSize(cp.src);
      const W = (nat?.width ?? cn.width) * cp.crop.scale,
        H = (nat?.height ?? cn.height) * cp.crop.scale;
      const m = mul(view, doc.worldTransform(cn.id));
      const corners = [apply(m, { x: cp.crop.x, y: cp.crop.y }), apply(m, { x: cp.crop.x + W, y: cp.crop.y }), apply(m, { x: cp.crop.x + W, y: cp.crop.y + H }), apply(m, { x: cp.crop.x, y: cp.crop.y + H })];
      if (img) {
        ctx.save();
        ctx.globalAlpha = 0.35;
        ctx.setTransform(m[0] * DPR, m[1] * DPR, m[2] * DPR, m[3] * DPR, m[4] * DPR, m[5] * DPR);
        ctx.drawImage(img, cp.crop.x, cp.crop.y, W, H);
        ctx.restore();
      }
      ctx.strokeStyle = SELECTION_COLOR;
      ctx.setLineDash([5, 4]);
      ctx.beginPath();
      corners.forEach((c, i) => (i ? ctx.lineTo(c.x, c.y) : ctx.moveTo(c.x, c.y)));
      ctx.closePath();
      ctx.stroke();
      ctx.setLineDash([]);
      this.badge(ctx, corners[0].x, corners[0].y - 22, 'Crop · drag to move, scroll to scale, Esc to finish', SELECTION_COLOR);
    }

    // Vector edit mode: anchors and Bézier handles
    const pe = app.ui.editingPathId ? doc.get(app.ui.editingPathId) : null;
    if (pe) {
      const m = mul(view, doc.worldTransform(pe.id));
      const cmds = pathCommands(pe.path);
      ctx.strokeStyle = SELECTION_COLOR;
      ctx.lineWidth = 1;
      strokeOutline(ctx, m, cmds);
      for (const a of anchorsOf(cmds)) {
        const p = apply(m, a.p);
        const sel = app.ui.pathAnchor === a.cmd || app.ui.pathAnchors.includes(a.cmd);
        for (const h of [a.hin, a.hout]) {
          if (!h || (!sel && app.ui.pathAnchor !== null && Math.abs(a.cmd - (app.ui.pathAnchor ?? -9)) > 1)) continue;
          const hp = apply(m, h);
          ctx.strokeStyle = SELECTION_COLOR;
          ctx.beginPath();
          ctx.moveTo(p.x, p.y);
          ctx.lineTo(hp.x, hp.y);
          ctx.stroke();
          ctx.fillStyle = '#fff';
          ctx.beginPath();
          ctx.arc(hp.x, hp.y, 3.5, 0, Math.PI * 2);
          ctx.fill();
          ctx.stroke();
        }
        ctx.fillStyle = sel ? SELECTION_COLOR : '#fff';
        ctx.strokeStyle = SELECTION_COLOR;
        ctx.beginPath();
        ctx.rect(Math.round(p.x) - 3.5, Math.round(p.y) - 3.5, 7, 7);
        ctx.fill();
        ctx.stroke();
      }
    }

    // Lasso
    const lasso = app.ui.lasso;
    if (lasso && lasso.length > 1) {
      ctx.beginPath();
      lasso.forEach((q, i) => {
        const a = apply(view, q);
        if (i) ctx.lineTo(a.x, a.y);
        else ctx.moveTo(a.x, a.y);
      });
      ctx.closePath();
      ctx.fillStyle = 'rgba(13,153,255,0.08)';
      ctx.fill();
      ctx.setLineDash([4, 3]);
      ctx.strokeStyle = SELECTION_COLOR;
      ctx.lineWidth = 1;
      ctx.stroke();
      ctx.setLineDash([]);
    }

    // Marquee
    const mq = app.ui.marquee;
    if (mq) {
      const a = apply(view, { x: mq.x, y: mq.y });
      ctx.fillStyle = 'rgba(13,153,255,0.08)';
      ctx.strokeStyle = SELECTION_COLOR;
      ctx.lineWidth = 1;
      ctx.fillRect(a.x, a.y, mq.width * app.camera.zoom, mq.height * app.camera.zoom);
      ctx.strokeRect(a.x + 0.5, a.y + 0.5, mq.width * app.camera.zoom, mq.height * app.camera.zoom);
    }

    // Snap guides
    ctx.strokeStyle = '#f24822';
    ctx.lineWidth = 1;
    for (const g of app.ui.guides) {
      ctx.beginPath();
      if (g.axis === 'x') {
        const a = apply(view, { x: g.pos, y: g.from });
        const b = apply(view, { x: g.pos, y: g.to });
        ctx.moveTo(Math.round(a.x) + 0.5, a.y);
        ctx.lineTo(Math.round(b.x) + 0.5, b.y);
      } else {
        const a = apply(view, { x: g.from, y: g.pos });
        const b = apply(view, { x: g.to, y: g.pos });
        ctx.moveTo(a.x, Math.round(a.y) + 0.5);
        ctx.lineTo(b.x, Math.round(b.y) + 0.5);
      }
      ctx.stroke();
    }

    // Auto-layout insertion line
    if (app.ui.insertLine) {
      const a = apply(view, app.ui.insertLine.a);
      const b = apply(view, app.ui.insertLine.b);
      ctx.strokeStyle = SELECTION_COLOR;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(a.x, a.y);
      ctx.lineTo(b.x, b.y);
      ctx.stroke();
    }

    // Drafts
    const d = app.ui.draft;
    if (d) {
      ctx.strokeStyle = d.kind === 'pencil' ? app.toolOptions.penColor : SELECTION_COLOR;
      ctx.lineWidth = d.kind === 'pencil' ? app.toolOptions.penWidth * app.camera.zoom : 1.5;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.beginPath();
      const pts = d.points.map((p) => apply(view, p));
      pts.forEach((p, i) => (i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y)));
      if (d.cursor && d.kind !== 'pencil') {
        const c = apply(view, d.cursor);
        ctx.lineTo(c.x, c.y);
      }
      ctx.stroke();
      if (d.kind === 'pen') {
        for (const p of pts) {
          ctx.fillStyle = '#fff';
          ctx.strokeStyle = SELECTION_COLOR;
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.arc(p.x, p.y, 3.5, 0, Math.PI * 2);
          ctx.fill();
          ctx.stroke();
        }
      }
      if (d.kind === 'connector') {
        for (const id of [d.startId, d.endId]) {
          const n = id ? doc.get(id) : null;
          if (n) {
            ctx.strokeStyle = SELECTION_COLOR;
            ctx.lineWidth = 2;
            this.outline(ctx, n, view);
          }
        }
      }
    }

    // Ruler guides
    const guides = [...(doc.get(pageId)?.guides ?? [])];
    const dg = app.ui.draggingGuide;
    ctx.lineWidth = 1;
    guides.forEach((g, i) => {
      if (dg && dg.index === i) return;
      this.guide(ctx, g.axis, g.pos, view, width, height, '#ff24bd');
    });
    if (dg) {
      this.guide(ctx, dg.axis, dg.pos, view, width, height, '#ff24bd');
      const p = apply(view, dg.axis === 'x' ? { x: dg.pos, y: 0 } : { x: 0, y: dg.pos });
      this.badge(ctx, dg.axis === 'x' ? p.x + 4 : 26, dg.axis === 'x' ? 26 : p.y + 4, String(Math.round(dg.pos)), '#ff24bd');
    }

    // ⌥ measurements
    if ((app.ui.altDown || app.ui.devMode) && selected.size && !app.ui.dragging) this.measure(ctx, view);
    // Outline mode (⌘Y): every visible layer as a hairline, culled to the viewport
    if (app.ui.outlineMode) {
      const vr = app.viewportRect();
      ctx.save();
      ctx.strokeStyle = '#7a7a7a';
      ctx.lineWidth = 1;
      doc.walk(pageId, (n) => {
        if (n.id === pageId) return;
        if (!n.visible) return false;
        const b = doc.worldBounds(n.id);
        if (b.x > vr.x + vr.width || b.y > vr.y + vr.height || b.x + b.width < vr.x || b.y + b.height < vr.y) return n.clipsContent ? false : undefined;
        this.outline(ctx, n, view);
      });
      ctx.restore();
    }
    // Video fills: a play badge (the canvas shows the poster; Present plays it)
    for (const id of this.videoNodes()) {
      const n = doc.get(id);
      if (!n || !n.visible || doc.pageOf(id)?.id !== pageId) continue;
      const b = doc.worldBounds(id);
      const s = apply(view, { x: b.x, y: b.y });
      const w = b.width * this.app.camera.zoom;
      if (w < 40) continue;
      ctx.save();
      ctx.fillStyle = 'rgba(0,0,0,0.55)';
      ctx.beginPath();
      ctx.arc(s.x + 18, s.y + 18, 12, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#fff';
      ctx.beginPath();
      ctx.moveTo(s.x + 14, s.y + 11);
      ctx.lineTo(s.x + 25, s.y + 18);
      ctx.lineTo(s.x + 14, s.y + 25);
      ctx.closePath();
      ctx.fill();
      ctx.restore();
    }
    // Emoji reactions rise and fade over 2 seconds
    if (app.reactions.length) {
      const now = performance.now();
      app.reactions = app.reactions.filter((r) => now - r.at < 2000);
      for (const r of app.reactions) {
        const t = (now - r.at) / 2000;
        const sp = apply(view, { x: r.x, y: r.y });
        ctx.save();
        ctx.globalAlpha = 1 - t * t;
        ctx.font = `${Math.round(40 + 16 * Math.sin(Math.min(1, t * 4) * Math.PI))}px "Apple Color Emoji", "Segoe UI Emoji", sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(r.emoji, sp.x + Math.sin(t * 6) * 10, sp.y - t * 120);
        ctx.restore();
      }
      if (app.reactions.length) requestAnimationFrame(() => app.requestRender());
    }
    // Dev Mode: annotation callouts
    if (app.ui.devMode) doc.walk(pageId, (n) => void (n.annotations?.length && n.visible && this.annotation(ctx, n, view)));

    // Remote cursors
    for (const p of app.peers.values()) {
      if (!p.cursor || (p.pageId && p.pageId !== pageId)) continue;
      const s = apply(view, p.cursor);
      this.cursor(ctx, s.x, s.y, p.color, p.status?.startsWith('💬') ? p.status.slice(2).trim() : p.name);
    }
  }

  private guide(ctx: CanvasRenderingContext2D, axis: 'x' | 'y', pos: number, view: Mat, w: number, h: number, color: string) {
    const p = apply(view, axis === 'x' ? { x: pos, y: 0 } : { x: 0, y: pos });
    ctx.strokeStyle = color;
    ctx.beginPath();
    if (axis === 'x') {
      ctx.moveTo(Math.round(p.x) + 0.5, 0);
      ctx.lineTo(Math.round(p.x) + 0.5, h);
    } else {
      ctx.moveTo(0, Math.round(p.y) + 0.5);
      ctx.lineTo(w, Math.round(p.y) + 0.5);
    }
    ctx.stroke();
  }

  private grids(ctx: CanvasRenderingContext2D, n: NodeData, view: Mat) {
    if (!n.visible) return;
    for (const c of this.app.doc.children(n.id)) if (c.type === 'FRAME' || c.type === 'COMPONENT' || c.type === 'SECTION') this.grids(ctx, c, view);
    if (!n.layoutGrids?.length) return;
    const m = mul(view, this.app.doc.worldTransform(n.id));
    ctx.save();
    ctx.setTransform(m[0] * DPR, m[1] * DPR, m[2] * DPR, m[3] * DPR, m[4] * DPR, m[5] * DPR);
    ctx.beginPath();
    ctx.rect(0, 0, n.width, n.height);
    ctx.clip();
    for (const g of n.layoutGrids) {
      if (g.visible === false) continue;
      const col = g.color ?? (g.pattern === 'GRID' ? { r: 1, g: 0, b: 0, a: 0.1 } : { r: 1, g: 0, b: 0, a: 0.1 });
      ctx.fillStyle = `rgba(${col.r * 255},${col.g * 255},${col.b * 255},${col.a})`;
      ctx.strokeStyle = `rgba(${col.r * 255},${col.g * 255},${col.b * 255},${Math.min(1, col.a * 2.5)})`;
      if (g.pattern === 'GRID') {
        const sz = Math.max(1, g.size ?? 8);
        if (sz * this.app.camera.zoom < 4) continue;
        ctx.lineWidth = 1 / (this.app.camera.zoom);
        ctx.beginPath();
        for (let x = sz; x < n.width; x += sz) {
          ctx.moveTo(x, 0);
          ctx.lineTo(x, n.height);
        }
        for (let y = sz; y < n.height; y += sz) {
          ctx.moveTo(0, y);
          ctx.lineTo(n.width, y);
        }
        ctx.stroke();
        continue;
      }
      const horizontal = g.pattern === 'COLUMNS';
      const total = horizontal ? n.width : n.height;
      const margin = g.margin ?? 0,
        gutter = g.gutter ?? 20;
      const count = Math.max(1, g.count ?? 12);
      const w = (total - margin * 2 - gutter * (count - 1)) / count;
      for (let i = 0; i < count; i++) {
        const a = margin + i * (w + gutter);
        if (horizontal) ctx.fillRect(a, 0, w, n.height);
        else ctx.fillRect(0, a, n.width, w);
      }
    }
    ctx.restore();
  }

  /** Red distance lines between the selection and the hovered layer (or the parent frame). */
  private measure(ctx: CanvasRenderingContext2D, view: Mat) {
    const app = this.app;
    const doc = app.doc;
    const sel = app.editor.selection;
    let a: Rect | null = null;
    for (const id of sel) {
      const b = doc.worldBounds(id);
      a = a ? union(a, b) : b;
    }
    if (!a) return;
    let targetId = app.ui.hoverId && !sel.includes(app.ui.hoverId) ? app.ui.hoverId : null;
    if (!targetId) {
      const p = doc.get(doc.get(sel[0])?.parent);
      if (p && p.type !== 'PAGE') targetId = p.id;
    }
    if (!targetId) return;
    const b = doc.worldBounds(targetId);
    const color = '#f24822';
    const z = app.camera.zoom;
    const line = (x0: number, y0: number, x1: number, y1: number, value: number) => {
      if (value < 0.01) return;
      const p0 = apply(view, { x: x0, y: y0 }),
        p1 = apply(view, { x: x1, y: y1 });
      ctx.strokeStyle = color;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(p0.x, p0.y);
      ctx.lineTo(p1.x, p1.y);
      ctx.stroke();
      this.badge(ctx, (p0.x + p1.x) / 2, (p0.y + p1.y) / 2 - 9, `${Math.round(value * 100) / 100}`, color, false, true);
    };
    const cx = a.x + a.width / 2,
      cy = a.y + a.height / 2;
    const inside = a.x >= b.x && a.y >= b.y && a.x + a.width <= b.x + b.width && a.y + a.height <= b.y + b.height;
    if (inside) {
      line(b.x, cy, a.x, cy, a.x - b.x);
      line(a.x + a.width, cy, b.x + b.width, cy, b.x + b.width - (a.x + a.width));
      line(cx, b.y, cx, a.y, a.y - b.y);
      line(cx, a.y + a.height, cx, b.y + b.height, b.y + b.height - (a.y + a.height));
    } else {
      // gaps between the boxes on each axis
      const yMid = Math.max(a.y, b.y) < Math.min(a.y + a.height, b.y + b.height) ? (Math.max(a.y, b.y) + Math.min(a.y + a.height, b.y + b.height)) / 2 : cy;
      const xMid = Math.max(a.x, b.x) < Math.min(a.x + a.width, b.x + b.width) ? (Math.max(a.x, b.x) + Math.min(a.x + a.width, b.x + b.width)) / 2 : cx;
      if (b.x > a.x + a.width) line(a.x + a.width, yMid, b.x, yMid, b.x - (a.x + a.width));
      if (b.x + b.width < a.x) line(b.x + b.width, yMid, a.x, yMid, a.x - (b.x + b.width));
      if (b.y > a.y + a.height) line(xMid, a.y + a.height, xMid, b.y, b.y - (a.y + a.height));
      if (b.y + b.height < a.y) line(xMid, b.y + b.height, xMid, a.y, a.y - (b.y + b.height));
    }
    // outline the target
    const tl = apply(view, { x: b.x, y: b.y });
    ctx.strokeStyle = color;
    ctx.setLineDash([4, 3]);
    ctx.strokeRect(tl.x + 0.5, tl.y + 0.5, b.width * z, b.height * z);
    ctx.setLineDash([]);
  }

  private title(ctx: CanvasRenderingContext2D, n: NodeData, view: Mat) {
    const isSection = n.type === 'SECTION';
    const m = mul(view, this.app.doc.worldTransform(n.id));
    const p = apply(m, { x: 0, y: 0 });
    const w = n.width * this.app.camera.zoom;
    if (w < 24) return;
    const selected = this.app.editor.selection.includes(n.id);
    const label = titleLabel(n);
    if (isSection) {
      ctx.save();
      ctx.font = '600 13px Inter, sans-serif';
      const tw = Math.min(ctx.measureText(label).width + 16, w);
      ctx.fillStyle = selected ? SELECTION_COLOR : '#ffffff';
      ctx.strokeStyle = selected ? SELECTION_COLOR : '#b3b3b3';
      ctx.beginPath();
      ctx.roundRect(p.x, p.y - 30, tw, 24, 6);
      ctx.fill();
      ctx.stroke();
      ctx.fillStyle = selected ? '#ffffff' : '#1e1e1e';
      ctx.textBaseline = 'middle';
      ctx.fillText(clip(ctx, label, tw - 16), p.x + 8, p.y - 18);
      ctx.restore();
      return;
    }
    ctx.fillStyle = selected ? SELECTION_COLOR : n.type === 'COMPONENT' || n.type === 'INSTANCE' ? COMPONENT_COLOR : '#808080';
    ctx.save();
    ctx.translate(p.x, p.y - 4);
    if (n.rotation) ctx.rotate((n.rotation * Math.PI) / 180);
    const shown = clip(ctx, label, n.devStatus ? w - 90 : w);
    ctx.fillText(shown, 0, 0);
    if (n.devStatus) {
      // hand-off badge after the title
      const x = ctx.measureText(shown).width + 6;
      const text = n.devStatus === 'READY' ? '</> Ready for dev' : '✓ Completed';
      ctx.font = '600 10px Inter, sans-serif';
      const tw = ctx.measureText(text).width + 10;
      ctx.fillStyle = n.devStatus === 'READY' ? '#14ae5c' : '#8a8a8a';
      ctx.beginPath();
      ctx.roundRect(x, -13, tw, 15, 4);
      ctx.fill();
      ctx.fillStyle = '#fff';
      ctx.fillText(text, x + 5, -2);
    }
    ctx.restore();
  }

  /** A Dev Mode note pinned to the right of its layer. */
  private annotation(ctx: CanvasRenderingContext2D, n: NodeData, view: Mat) {
    const b = this.app.doc.worldBounds(n.id);
    const anchor = apply(view, { x: b.x + b.width, y: b.y + b.height / 2 });
    ctx.save();
    ctx.font = '500 11px Inter, sans-serif';
    const lines = n.annotations!.map((a) => (a.props?.length ? `${a.label}  · ${a.props.join(', ')}` : a.label));
    const w = Math.min(260, Math.max(...lines.map((l) => ctx.measureText(l).width)) + 16);
    const h = lines.length * 16 + 10;
    const x = anchor.x + 24,
      y = anchor.y - h / 2;
    ctx.strokeStyle = '#9747ff';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(anchor.x, anchor.y);
    ctx.lineTo(x, anchor.y);
    ctx.stroke();
    ctx.fillStyle = '#9747ff';
    ctx.beginPath();
    ctx.arc(anchor.x, anchor.y, 3, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.roundRect(x, y, w, h, 6);
    ctx.fill();
    ctx.fillStyle = '#fff';
    ctx.textBaseline = 'top';
    lines.forEach((l, i) => ctx.fillText(clip(ctx, l, w - 16), x + 8, y + 6 + i * 16));
    ctx.restore();
  }

  outline(ctx: CanvasRenderingContext2D, n: NodeData, view: Mat) {
    if (n.type === 'CONNECTOR') {
      const g = connectorGeometry(this.app.doc, n);
      const pts = g.points.map((p) => apply(view, p));
      ctx.beginPath();
      if (g.kind === 'CURVED') {
        ctx.moveTo(pts[0].x, pts[0].y);
        ctx.bezierCurveTo(pts[1].x, pts[1].y, pts[2].x, pts[2].y, pts[3].x, pts[3].y);
      } else pts.forEach((p, i) => (i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y)));
      ctx.stroke();
      return;
    }
    const m = mul(view, this.app.doc.worldTransform(n.id));
    // text shows as plain dark glyphs, like Figma's outline mode
    if ((n.type === 'TEXT' || n.type === 'STICKY' || n.type === 'SHAPE_WITH_TEXT') && n.characters) this.outlineText(ctx, n, m);
    if (n.type === 'TEXT') return;
    if ( n.type === 'GROUP' || n.type === 'FRAME' || n.type === 'COMPONENT' || n.type === 'INSTANCE' || n.type === 'IMAGE' || n.type === 'STICKY' || n.type === 'SECTION') {
      strokeOutline(ctx, m, [
        { c: 'M', x: 0, y: 0 },
        { c: 'L', x: n.width, y: 0 },
        { c: 'L', x: n.width, y: n.height },
        { c: 'L', x: 0, y: n.height },
        { c: 'Z' },
      ]);
      return;
    }
    const o = nodeOutline(n);
    if (o) strokeOutline(ctx, m, o);
  }

  private outlineText(ctx: CanvasRenderingContext2D, n: NodeData, m: Mat) {
    const layout = layoutNodeText(n);
    const box = n.type === 'TEXT' ? { x: 0, y: 0 } : textBox(n);
    ctx.save();
    ctx.setTransform(m[0] * DPR, m[1] * DPR, m[2] * DPR, m[3] * DPR, m[4] * DPR, m[5] * DPR);
    ctx.translate(box.x, box.y);
    ctx.fillStyle = '#333';
    ctx.textBaseline = 'alphabetic';
    for (const line of layout.lines) {
      if (line.pieces && layout.styles) {
        for (const piece of line.pieces) {
          const st = layout.styles[piece.style];
          fillTextRun(ctx, piece.text, line.x + piece.x, line.baseline, st.font, st.letterSpacing, st.features);
        }
      } else if (line.pieces) for (const p of line.pieces) fillTextRun(ctx, p.text, line.x + p.x, line.baseline, layout.font, layout.style.letterSpacing, layout.style.features);
      else fillTextRun(ctx, line.text, line.x, line.baseline, layout.font, layout.style.letterSpacing, layout.style.features);
    }
    ctx.restore();
  }

  private connectorHandles(ctx: CanvasRenderingContext2D, n: NodeData, view: Mat) {
    const g = connectorGeometry(this.app.doc, n);
    for (const p of [g.points[0], g.points[g.points.length - 1]]) {
      const s = apply(view, p);
      ctx.fillStyle = '#fff';
      ctx.strokeStyle = SELECTION_COLOR;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.arc(s.x, s.y, 5, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
    }
  }

  private lineHandles(ctx: CanvasRenderingContext2D, n: NodeData, view: Mat, color: string) {
    const m = mul(view, this.app.doc.worldTransform(n.id));
    for (const p of [apply(m, { x: 0, y: 0 }), apply(m, { x: n.width, y: 0 })]) {
      ctx.fillStyle = '#fff';
      ctx.strokeStyle = color;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.rect(p.x - 4 + 0.5, p.y - 4 + 0.5, 8, 8);
      ctx.fill();
      ctx.stroke();
    }
  }

  badge(ctx: CanvasRenderingContext2D, x: number, y: number, text: string, color: string, above = false, center = false) {
    ctx.save();
    ctx.font = '500 11px Inter, sans-serif';
    const w = ctx.measureText(text).width + 10;
    const h = 18;
    const bx = center ? x - w / 2 : x;
    const by = above ? y - h : y;
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.roundRect(bx, by, w, h, 4);
    ctx.fill();
    ctx.fillStyle = '#fff';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, bx + 5, by + h / 2 + 0.5);
    ctx.restore();
  }

  private cursor(ctx: CanvasRenderingContext2D, x: number, y: number, color: string, name: string) {
    ctx.save();
    ctx.translate(x, y);
    ctx.fillStyle = color;
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(0, 16);
    ctx.lineTo(4.5, 12);
    ctx.lineTo(8, 19);
    ctx.lineTo(10.5, 18);
    ctx.lineTo(7, 11);
    ctx.lineTo(12.5, 11);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    this.badge(ctx, x + 10, y + 20, name, color);
  }
}

function fmt(v: number) {
  return Math.round(v * 100) / 100 + '';
}

function clip(ctx: CanvasRenderingContext2D, s: string, w: number) {
  if (ctx.measureText(s).width <= w) return s;
  let lo = 0,
    hi = s.length;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (ctx.measureText(s.slice(0, mid) + '…').width <= w) lo = mid;
    else hi = mid - 1;
  }
  return s.slice(0, lo) + '…';
}

export type { NodeId };
